import { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { logger } from './utils/logger';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// ============================================================
// Hiring Manager Portal
// ============================================================
export function HiringManagerPortal() {
  const [data, setData] = useState(null);
  const [loadError, setLoadError] = useState(null);
  const [loadStatus, setLoadStatus] = useState(null);
  const [selectedAppId, setSelectedAppId] = useState(null);
  const [view, setView] = useState('all_candidates');  // all_candidates | by_req | interviews
  const [activeReqId, setActiveReqId] = useState(null);

  const load = useCallback(async () => {
    setLoadError(null);
    try {
      const { data } = await axios.get(`${API}/hm/dashboard`);
      setData(data);
    } catch (err) {
      logger.error(err);
      setLoadStatus(err.response?.status);
      setLoadError(err.response?.data?.detail || err.message || 'Failed to load dashboard');
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  if (loadError) {
    const isAuth = loadStatus === 401;
    const isForbidden = loadStatus === 403;
    return (
      <div className="p-8 max-w-2xl mx-auto">
        <div className={`border rounded-lg p-6 ${isAuth ? 'bg-amber-50 border-amber-200' : 'bg-red-50 border-red-200'}`}>
          <h2 className={`font-semibold mb-2 ${isAuth ? 'text-amber-900' : 'text-red-800'}`}>
            {isAuth ? 'Your session has expired' : isForbidden ? 'Access denied' : "Couldn't load your dashboard"}
          </h2>
          <p className={`text-sm mb-3 ${isAuth ? 'text-amber-800' : 'text-red-700'}`}>
            {isAuth
              ? 'Please log in again to view your hiring manager dashboard.'
              : isForbidden
              ? `This account doesn't have hiring manager permissions. ${loadError}`
              : loadError}
          </p>
          <div className="flex gap-2">
            {isAuth ? (
              <a href={`/login?redirect=${encodeURIComponent('/hiring-manager')}`} className="px-4 py-1.5 bg-amber-600 text-white text-sm rounded hover:bg-amber-700" data-testid="hm-relogin-button">Log in again</a>
            ) : (
              <button onClick={load} className="px-3 py-1.5 bg-red-600 text-white text-sm rounded hover:bg-red-700" data-testid="hm-retry-button">Try again</button>
            )}
            <a href="/" className="px-3 py-1.5 bg-white border border-gray-300 text-sm rounded hover:bg-gray-50">Home</a>
          </div>
        </div>
      </div>
    );
  }
  if (!data) return <div className="p-8 text-center text-gray-500">Loading your dashboard…</div>;

  const reqApps = activeReqId ? data.applications.filter(a => a.requisition_id === activeReqId) : data.applications;

  return (
    <div className="min-h-screen bg-gray-50" data-testid="hm-portal">
      <main className="max-w-7xl mx-auto px-6 py-6">
        <div className="mb-6">
          <h1 className="text-3xl font-bold text-gray-900">Hiring Manager Dashboard</h1>
          <p className="text-sm text-gray-600 mt-1">Review your candidates, leave feedback, and make hiring decisions. Recruiters handle the workflow — you focus on people.</p>
        </div>

        {/* Stats */}
        <div className="grid grid-cols-2 md:grid-cols-5 gap-3 mb-6">
          <StatCard label="My Requisitions" value={data.requisitions.length} colour="indigo" />
          <StatCard label="Active Candidates" value={data.active} colour="blue" />
          <StatCard label="Pending Decision" value={data.pending_decisions} colour="amber" testid="hm-pending-decisions" />
          <StatCard label="Shortlisted" value={data.shortlisted} colour="green" />
          <StatCard label="Upcoming Interviews" value={data.upcoming_interviews} colour="purple" />
        </div>

        {/* View switcher */}
        <div className="flex gap-2 mb-4">
          {[{id:'all_candidates',label:'All Candidates'},{id:'by_req',label:'By Requisition'},{id:'interviews',label:'My Interviews'}].map(v => (
            <button
              key={v.id}
              onClick={() => setView(v.id)}
              className={`px-4 py-2 rounded text-sm ${view === v.id ? 'bg-indigo-600 text-white' : 'bg-white border border-gray-300'}`}
              data-testid={`hm-view-${v.id}`}
            >
              {v.label}
            </button>
          ))}
        </div>

        {view === 'all_candidates' && (
          <div className="bg-white rounded-lg shadow overflow-hidden">
            <table className="w-full text-sm">
              <thead className="bg-gray-50 text-xs uppercase">
                <tr>
                  <th className="px-4 py-2 text-left">Candidate</th>
                  <th className="px-4 py-2 text-left">Role</th>
                  <th className="px-4 py-2 text-left">Stage</th>
                  <th className="px-4 py-2 text-left">Status</th>
                  <th className="px-4 py-2 text-left">My Decision</th>
                  <th className="px-4 py-2"></th>
                </tr>
              </thead>
              <tbody>
                {data.applications.map(a => (
                  <tr key={a.id} className="border-t hover:bg-gray-50">
                    <td className="px-4 py-2 font-medium">{a.candidate_name}</td>
                    <td className="px-4 py-2 text-gray-600">{a.requisition_title}</td>
                    <td className="px-4 py-2"><span className="px-2 py-0.5 rounded bg-blue-100 text-blue-700 text-xs">{a.stage}</span></td>
                    <td className="px-4 py-2">
                      <span className={`px-2 py-0.5 rounded text-xs ${
                        a.status === 'rejected' ? 'bg-red-100 text-red-700' :
                        a.status === 'on_hold' ? 'bg-amber-100 text-amber-700' :
                        'bg-green-100 text-green-700'
                      }`}>{a.status}</span>
                    </td>
                    <td className="px-4 py-2 text-xs">{a.last_decision ? <DecisionBadge decision={a.last_decision} /> : <span className="text-gray-400">— none —</span>}</td>
                    <td className="px-4 py-2 text-right">
                      <button onClick={() => setSelectedAppId(a.id)} className="px-3 py-1 bg-indigo-600 text-white text-xs rounded" data-testid={`hm-open-app-${a.id}`}>Review</button>
                    </td>
                  </tr>
                ))}
                {data.applications.length === 0 && (
                  <tr><td colSpan="6" className="px-4 py-10 text-center text-gray-400">No candidates yet. The recruiter will add them as applications arrive.</td></tr>
                )}
              </tbody>
            </table>
          </div>
        )}

        {view === 'by_req' && (
          <div>
            <div className="flex flex-wrap gap-2 mb-4">
              {data.requisitions.map(r => (
                <button
                  key={r.id}
                  onClick={() => setActiveReqId(activeReqId === r.id ? null : r.id)}
                  className={`px-3 py-1.5 rounded text-sm ${activeReqId === r.id ? 'bg-indigo-600 text-white' : 'bg-white border border-gray-300'}`}
                >
                  {r.title} <span className="text-xs opacity-70">({data.applications.filter(a => a.requisition_id === r.id).length})</span>
                </button>
              ))}
            </div>
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-3">
              {reqApps.map(a => (
                <div key={a.id} className="bg-white rounded-lg shadow p-4 border-l-4 border-indigo-500 cursor-pointer hover:shadow-md" onClick={() => setSelectedAppId(a.id)}>
                  <div className="flex justify-between items-start">
                    <div>
                      <p className="font-semibold">{a.candidate_name}</p>
                      <p className="text-xs text-gray-500">{a.requisition_title}</p>
                    </div>
                    {a.last_decision && <DecisionBadge decision={a.last_decision} />}
                  </div>
                  <div className="flex gap-1 mt-2 flex-wrap">
                    <span className="px-2 py-0.5 rounded bg-blue-100 text-blue-700 text-xs">{a.stage}</span>
                    {a.shortlisted && <span className="px-2 py-0.5 rounded bg-green-100 text-green-700 text-xs">⭐ Shortlisted</span>}
                  </div>
                </div>
              ))}
              {reqApps.length === 0 && <p className="text-sm text-gray-400 col-span-3 text-center py-8">No candidates on this requisition.</p>}
            </div>
          </div>
        )}

        {view === 'interviews' && (
          <div className="space-y-2">
            {data.interviews.map(iv => (
              <div key={iv.id} className="bg-white rounded-lg shadow p-4 border-l-4 border-purple-500">
                <div className="flex justify-between items-center">
                  <div>
                    <p className="font-semibold">{iv.title}</p>
                    <p className="text-sm text-gray-600">
                      {new Date(iv.scheduled_time).toLocaleString()} · {iv.duration_minutes}min · <strong>{iv.candidate_name}</strong> for <strong>{iv.requisition_title}</strong>
                    </p>
                    {iv.meeting_link && <a href={iv.meeting_link} target="_blank" rel="noreferrer" className="text-xs text-indigo-600 underline">{iv.platform}</a>}
                  </div>
                  <a href={`${API}/cockpit/interviews/${iv.id}/ics`} target="_blank" rel="noreferrer" className="text-xs px-2 py-1 bg-indigo-100 text-indigo-700 rounded">📅 .ics</a>
                </div>
              </div>
            ))}
            {data.interviews.length === 0 && <p className="text-sm text-gray-400 text-center py-8 bg-white rounded-lg shadow">No upcoming interviews.</p>}
          </div>
        )}
      </main>

      {selectedAppId && (
        <ApplicationReviewPanel applicationId={selectedAppId} onClose={() => setSelectedAppId(null)} onChange={load} />
      )}
    </div>
  );
}

function StatCard({label, value, colour, testid}) {
  const map = {indigo: 'text-indigo-700', blue: 'text-blue-700', amber: 'text-amber-700', green: 'text-green-700', purple: 'text-purple-700'};
  return (
    <div className="bg-white rounded-lg shadow p-4" data-testid={testid}>
      <p className={`text-3xl font-bold ${map[colour] || 'text-gray-700'}`}>{value}</p>
      <p className="text-xs text-gray-500 mt-1">{label}</p>
    </div>
  );
}

function DecisionBadge({decision}) {
  const styles = {
    shortlist: 'bg-green-100 text-green-700',
    strong_yes: 'bg-green-200 text-green-800',
    reject: 'bg-red-100 text-red-700',
    strong_no: 'bg-red-200 text-red-800',
    hold: 'bg-amber-100 text-amber-700',
    advance: 'bg-indigo-100 text-indigo-700'
  };
  const labels = {shortlist: '⭐ Shortlist', strong_yes: '💚 Strong Yes', reject: '✗ Reject', strong_no: '🚫 Strong No', hold: '⏸ Hold', advance: '→ Advance'};
  return <span className={`px-2 py-0.5 rounded text-xs ${styles[decision] || 'bg-gray-100'}`}>{labels[decision] || decision}</span>;
}

// ============================================================
// Application Review Panel — comments + decisions + read-only candidate view
// ============================================================
function ApplicationReviewPanel({applicationId, onClose, onChange}) {
  const [detail, setDetail] = useState(null);
  const [loadError, setLoadError] = useState(null);
  const [newComment, setNewComment] = useState('');
  const [mentions, setMentions] = useState([]);
  const [submitting, setSubmitting] = useState(false);

  const load = useCallback(async () => {
    setLoadError(null);
    try {
      const { data } = await axios.get(`${API}/hm/applications/${applicationId}`);
      setDetail(data);
    } catch (err) {
      setLoadError(err.response?.data?.detail || err.message || 'Failed to load application');
    }
  }, [applicationId]);

  useEffect(() => { load(); }, [load]);

  // Parse @mentions from comment text
  useEffect(() => {
    const matches = newComment.match(/@[\w._%+-]+@[\w.-]+\.[A-Za-z]{2,}/g) || [];
    setMentions(matches.map(m => m.slice(1).toLowerCase()));
  }, [newComment]);

  const postComment = async () => {
    if (!newComment.trim()) return;
    setSubmitting(true);
    try {
      await axios.post(`${API}/cockpit/applications/${applicationId}/comments`, {body: newComment, mentions});
      setNewComment('');
      load();
    } catch (err) {
      alert('Failed: ' + (err.response?.data?.detail || err.message));
    } finally {
      setSubmitting(false);
    }
  };

  const recordDecision = async (decision, reason = '') => {
    if ((decision === 'reject' || decision === 'strong_no') && !window.confirm(`Confirm "${decision.replace('_', ' ')}" for ${detail?.candidate?.full_name}?`)) return;
    try {
      await axios.post(`${API}/cockpit/applications/${applicationId}/decision`, {decision, reason});
      load();
      onChange();
    } catch (err) {
      alert('Failed: ' + (err.response?.data?.detail || err.message));
    }
  };

  if (loadError) {
    return (
      <div className="fixed inset-0 bg-black/40 z-50 flex justify-end" onClick={onClose}>
        <div className="bg-white w-full max-w-md p-6" onClick={e => e.stopPropagation()}>
          <div className="bg-red-50 border border-red-200 rounded p-4">
            <p className="font-semibold text-red-800">Couldn't load application</p>
            <p className="text-sm text-red-700 mt-1">{loadError}</p>
            <button onClick={onClose} className="mt-3 px-3 py-1.5 bg-red-600 text-white text-sm rounded">Close</button>
          </div>
        </div>
      </div>
    );
  }
  if (!detail) return (
    <div className="fixed inset-0 bg-black/40 z-50 flex justify-end" onClick={onClose}>
      <div className="bg-white w-full max-w-md p-6 text-center text-gray-500" onClick={e => e.stopPropagation()}>Loading application…</div>
    </div>
  );

  return (
    <div className="fixed inset-0 bg-black/40 z-50 flex justify-end" onClick={onClose}>
      <div className="bg-white w-full max-w-2xl h-full overflow-y-auto shadow-2xl" onClick={e => e.stopPropagation()} data-testid="hm-review-panel">
        <header className="sticky top-0 bg-white border-b p-5 flex justify-between items-start z-10">
          <div>
            <h2 className="text-xl font-semibold">{detail.candidate.full_name}</h2>
            <p className="text-sm text-gray-600 mt-1">{detail.requisition?.title} · {detail.application.stage}</p>
            <div className="flex gap-1 mt-2 flex-wrap">
              {detail.application.shortlisted && <span className="px-2 py-0.5 rounded bg-green-100 text-green-700 text-xs">⭐ Shortlisted</span>}
              {detail.application.status === 'rejected' && <span className="px-2 py-0.5 rounded bg-red-100 text-red-700 text-xs">Rejected</span>}
              {detail.application.status === 'on_hold' && <span className="px-2 py-0.5 rounded bg-amber-100 text-amber-700 text-xs">On hold</span>}
              {detail.application.last_decision && <DecisionBadge decision={detail.application.last_decision} />}
            </div>
          </div>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-600 text-2xl leading-none">×</button>
        </header>

        {/* Quick Decision bar */}
        <div className="p-5 border-b bg-gray-50">
          <p className="text-xs font-semibold uppercase text-gray-500 mb-2">Your Decision</p>
          <div className="flex flex-wrap gap-2">
            <button onClick={() => recordDecision('strong_yes')} className="px-3 py-1.5 bg-green-700 text-white text-sm rounded hover:bg-green-800" data-testid="hm-decision-strong_yes">💚 Strong Yes</button>
            <button onClick={() => recordDecision('shortlist')} className="px-3 py-1.5 bg-green-600 text-white text-sm rounded hover:bg-green-700" data-testid="hm-decision-shortlist">⭐ Shortlist</button>
            <button onClick={() => recordDecision('hold')} className="px-3 py-1.5 bg-amber-500 text-white text-sm rounded hover:bg-amber-600" data-testid="hm-decision-hold">⏸ Hold</button>
            <button onClick={() => recordDecision('reject')} className="px-3 py-1.5 bg-red-500 text-white text-sm rounded hover:bg-red-600" data-testid="hm-decision-reject">✗ Reject</button>
            <button onClick={() => recordDecision('strong_no')} className="px-3 py-1.5 bg-red-700 text-white text-sm rounded hover:bg-red-800" data-testid="hm-decision-strong_no">🚫 Strong No</button>
          </div>
        </div>

        {/* AI Profile */}
        {detail.ai_profile && (
          <section className="p-5 border-b">
            <h3 className="font-semibold text-sm mb-3 flex items-center gap-2">
              🛡️ Candidate-Approved AI Profile
              <span className="text-xs px-2 py-0.5 bg-indigo-100 text-indigo-700 rounded">v{detail.ai_profile.version}</span>
            </h3>
            <div className="prose prose-sm max-w-none whitespace-pre-wrap text-sm text-gray-800 bg-indigo-50 p-3 rounded border border-indigo-200">
              {detail.ai_profile.content}
            </div>
            <p className="text-xs text-gray-400 mt-2 font-mono">Hash: {detail.ai_profile.content_hash?.slice(0, 24)}…</p>
          </section>
        )}

        {/* Interviews */}
        {detail.interviews.length > 0 && (
          <section className="p-5 border-b">
            <h3 className="font-semibold text-sm mb-3">Interviews ({detail.interviews.length})</h3>
            <div className="space-y-2">
              {detail.interviews.map(iv => (
                <div key={iv.id} className="text-sm bg-gray-50 p-3 rounded border">
                  <div className="flex justify-between items-start">
                    <div>
                      <p className="font-medium">{iv.title}</p>
                      <p className="text-xs text-gray-600">{new Date(iv.scheduled_time).toLocaleString()} · {iv.duration_minutes}min · {iv.platform}</p>
                    </div>
                    <span className={`text-xs px-2 py-0.5 rounded ${iv.status === 'cancelled' ? 'bg-red-100 text-red-700' : 'bg-green-100 text-green-700'}`}>{iv.status}</span>
                  </div>
                </div>
              ))}
            </div>
          </section>
        )}

        {/* Comments */}
        <section className="p-5 border-b">
          <h3 className="font-semibold text-sm mb-3">Discussion ({detail.comments.length})</h3>
          <div className="space-y-3 mb-4">
            {detail.comments.map(c => (
              <div key={c.id} className="bg-gray-50 p-3 rounded border-l-2 border-indigo-400" data-testid={`hm-comment-${c.id}`}>
                <div className="flex justify-between items-start mb-1">
                  <div>
                    <span className="text-sm font-medium">{c.author_name}</span>
                    <span className="ml-2 text-xs px-1.5 py-0.5 rounded bg-white border text-gray-600">{c.author_role}</span>
                  </div>
                  <span className="text-xs text-gray-400">{new Date(c.created_at).toLocaleString()}</span>
                </div>
                <p className="text-sm text-gray-800 whitespace-pre-wrap">{c.body}</p>
                {c.mentions?.length > 0 && (
                  <div className="mt-2 flex gap-1 flex-wrap">
                    {c.mentions.map(m => <span key={m} className="text-xs px-1.5 py-0.5 bg-indigo-100 text-indigo-700 rounded">@{m}</span>)}
                  </div>
                )}
              </div>
            ))}
            {detail.comments.length === 0 && <p className="text-sm text-gray-400">No comments yet. Start the discussion.</p>}
          </div>
          <div className="bg-white border rounded p-2">
            <textarea
              value={newComment}
              onChange={e => setNewComment(e.target.value)}
              placeholder="Share feedback. Use @email@domain.com to mention someone."
              rows="3"
              className="w-full px-2 py-1 text-sm focus:outline-none resize-none"
              data-testid="hm-comment-input"
            />
            <div className="flex justify-between items-center mt-2">
              <div className="text-xs text-gray-500">
                {mentions.length > 0 ? <>Mentions: {mentions.map(m => <span key={m} className="px-1.5 py-0.5 bg-indigo-50 text-indigo-700 rounded mr-1">@{m}</span>)}</> : 'Tip: use @email to notify someone'}
              </div>
              <button onClick={postComment} disabled={!newComment.trim() || submitting} className="px-3 py-1 bg-indigo-600 text-white text-xs rounded disabled:opacity-50" data-testid="hm-comment-submit">
                {submitting ? 'Posting…' : 'Post Comment'}
              </button>
            </div>
          </div>
        </section>

        {/* Decision history */}
        {detail.decisions.length > 0 && (
          <section className="p-5">
            <h3 className="font-semibold text-sm mb-3">Decision History</h3>
            <div className="space-y-2">
              {detail.decisions.map(d => (
                <div key={d.id} className="flex justify-between items-start text-sm bg-gray-50 p-3 rounded">
                  <div>
                    <DecisionBadge decision={d.decision} />
                    {d.reason && <p className="text-xs text-gray-600 mt-1">{d.reason}</p>}
                  </div>
                  <div className="text-right text-xs text-gray-500">
                    <p>{d.by_name}</p>
                    <p>{d.by_role}</p>
                    <p>{new Date(d.created_at).toLocaleString()}</p>
                  </div>
                </div>
              ))}
            </div>
          </section>
        )}
      </div>
    </div>
  );
}
